import {
    IColumn,
    IContextualMenuItem, Selection, SelectionMode
} from "@fluentui/react";
import { RouterParams } from "App";
import { RootState } from "app/store";
import CustomDetailList from "components/controls/CustomDetailList";
import { DefaultGridConfig } from "components/controls/CustomDetailList/CustomDetailList";
import { ICustomDetailListConfiguration } from "components/controls/CustomDetailList/ICustomDetailListConfiguration";
import { IGridItem } from "components/controls/CustomDetailList/IGridItem";
import CustomPanel from "components/controls/CustomPanel";
import { DetailListExcelGenerationAsync, createAttributeSet, createAttributesetColumns } from "global/Application";
import { useApplication } from "global/Application/Context";
import { addUpdateAttributeSet } from "global/Reducer/dataSlice";
import { updateCustomDetailListConfiguration } from "global/Reducer/statusSlice";
import { copyPropertiesOnly } from "global/Utility/TSFunctions";
import { IVisibilityScope } from "global/Visibility/IVisibilityScope.type";
import * as IXrmTypes from 'model/CRM';
import * as FAM from 'model/FlexibleAttributeModel';
import { IGroupConfigurationData } from "model/FlexibleAttributeModel";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router";
import AttributeSetGridViewEditor from "./AttributeSetGridViewEditor";

type TFlexibleFieldSetGridItem = IGridItem<FAM.IFlexibleFieldSet>;

interface IProps {
    label?: string;
    configAttribute?: IXrmTypes.bcbs_config_attribute;
    masterAttribute?: IXrmTypes.bcbs_config_masterattribute;
    category: FAM.IBenefit;
    snap?: IGroupConfigurationData;
    scope: FAM.IGroupConfigurationData | FAM.IPlan | FAM.IBenefit | FAM.IFeesAndPremiums;
    disabled?: boolean;
    cancellation?: boolean;
    visibility?: IVisibilityScope;
}

const AttributeSetGridView: React.FC<IProps> = (props) => {
    const { masterAttribute: ownerAttribute } = props;
    const application = useApplication();
    const [selectionCount, setSelectionCount] = useState(0);
    const state = useSelector((state: RootState) => state);

    const params = useParams<RouterParams>()

    const history = useHistory();

    const [editorIsMounted, setEditorIsMounted] = useState(false);
    const [editorIsOpen, _setEditorIsOpen] = useState(false);
    const setEditorIsOpen: React.Dispatch<React.SetStateAction<boolean>> = (value) => {
        if (!editorIsMounted)
            setEditorIsMounted(true);
        _setEditorIsOpen(value);
    }


    const dispatch = useDispatch();

    const [ffs, setFFS] = useState<FAM.IFlexibleFieldSet | null>(null);

    const storageId = `attributeset.grid(${props.masterAttribute?.bcbs_config_masterattributeid})`;

    const scopeGridConfig = () => {
        const u = state.status.gridConfigs[storageId];
        if (u)
            return u;

        const t = DefaultGridConfig();
        t.sort = [{ column: { key: 'attributeSetName' }, isSortedDescending: false }];

        return t;
    }

    const [gridConfig, setGridConfig] = useState<ICustomDetailListConfiguration>(scopeGridConfig());
    const gridConfigRef = useRef(gridConfig);

    // check if it has changed since mounting
    if (gridConfig !== gridConfigRef.current) {
        // console.log("render:grid config updated", gridConfig);

        gridConfigRef.current = gridConfig;

        // snapshot for bind back
        dispatch(updateCustomDetailListConfiguration({ id: storageId, gridConfig: structuredClone(gridConfig) }));
    }

    const onColumnClick = (event: React.MouseEvent<HTMLElement, MouseEvent>, column: IColumn) => {
        setGridConfig(t => {

            const gridConfig: ICustomDetailListConfiguration = { ...t };

            gridConfig.sort = [{
                column: { key: column.key },
                isSortedDescending: !t?.sort?.[0]?.isSortedDescending ?? false,
            }];


            return gridConfig;
        });
    };


    const columns = useMemo(() => {

        const attributeSetId = props.masterAttribute?.bcbs_config_masterattributeid!;

        const columns = createAttributesetColumns(
            state.data.config,
            attributeSetId,
            {
                tierNames: {}
            },
            gridConfig,
            onColumnClick,
        );

        return columns;
    },
        [
            gridConfig,
            gridConfig.filter,
            gridConfig.sort,
        ]
    );

    const category = props.category;
    const scope = props.scope;

    const snap: IGroupConfigurationData = props.snap || state.data.json;

    const scopeFlexibleFieldSets = useMemo(() => {
        let target = [] as FAM.IFlexibleFieldSet[];

        const categoryMapByName = state.data.config.cache.map_bcbs_config_category_named;

        if (scope && scope.id)
            if (category.bcbs_config_categoryid === categoryMapByName['Group Configuration'].bcbs_config_categoryid) {
                // Group Configuration
                target = snap.attributeSets

            } else if (category.bcbs_config_categoryid === categoryMapByName['Plan'].bcbs_config_categoryid) {
                // Plan
                target = snap.plans.find(q => q.id === scope.id)?.attributeSets ?? [];

            } else if (category.bcbs_config_categoryid === categoryMapByName["Fees and Premiums"].bcbs_config_categoryid) {
                // Fees And Premiums
                target = snap.feesAndPremiums.find(p => p.id === scope.id)?.attributeSets ?? [];

            } else if (category.bcbs_config_categoryid === categoryMapByName["Group Number"].bcbs_config_categoryid) {
                // sub groups
                // target = state.groupStructure.groupNumbers.find(p => p.id === scope.id)
                throw new Error('AttributeSet update has not been implemented for group-structure.');

            } else if (category.bcbs_config_categoryid === categoryMapByName["Stop Loss"].bcbs_config_categoryid) {
                // stop loss
                target = snap.stopLoss.find(p => p.id === scope.id)?.attributeSets ?? [];

            } else if (category.bcbs_config_categoryid === categoryMapByName["Accounts Receivable"].bcbs_config_categoryid) {
                //claims AR
                target = snap.claimsARs.find(p => p.id === scope.id)?.attributeSets ?? [];

            } else if (state.data.config.cache.bcbs_config_category_benefits.some(q => q.bcbs_config_categoryid === category.bcbs_config_categoryid)) {
                target = snap.benefits.find(p => p.id === scope.id)?.attributeSets ?? [];

            } else if (category.bcbs_config_categoryid === "nonstandard") {
                throw new Error('AttributeSet update has not been implemented for non-standard.');
            }

        const scopeFlexibleFieldSets = target.filter(q => q.attributeSetRef.id === props.masterAttribute?.bcbs_config_masterattributeid);
        return scopeFlexibleFieldSets;

    }, [scope]);

    const [items, itemsPaged] = useMemo(() => {

        const items: TFlexibleFieldSetGridItem[] = [];

        const gridConfigMappedColumns = {
            filterColumns: gridConfig.filter.map(q => columns?.find(w => w.key === q.column.key)),
            sortColumns: gridConfig.sort.map(q => columns?.find(w => w.key === q.column.key)),
        }

        scopeFlexibleFieldSets?.forEach((eFieldSet, ix1) => {

            // skip deactivated records/only show deactivated records if permitted at group config properties level
            if (eFieldSet.deactivated !== state.data.json.showInActives?.value) {
                return;
            }

            const item: TFlexibleFieldSetGridItem = {
                _index: ix1,
                key: eFieldSet.id,
                data: eFieldSet,
                name: eFieldSet.name,
            };

            eFieldSet.fields.forEach(eachField => {
                const configAttributeId = eachField.configRef.id;
                eachField.values.forEach(eachFieldValue => {
                    const key = `${configAttributeId}-${eachFieldValue?.tierRef?.id ?? ''}`
                    const field = eachField.values[0];
                    item[key] = field.value;
                });
            })


            // Filtering :
            let include = true;
            if (gridConfig && gridConfig.filter && gridConfig.filter.length > 0) {
                gridConfig.filter.forEach((filter, ix) => {
                    const column = gridConfigMappedColumns.filterColumns[ix];
                    if (column) {
                        const predicate = column.filterPredicate;
                        if (predicate)
                            include = include && predicate(item, filter.value, column);
                    }
                });
            }

            if (include)
                items.push(item);
        });

        // Sorting :
        if (gridConfig && gridConfig.sort && gridConfig.sort.length > 0)
            items.sort((a, b) => {

                let ret = 0;

                gridConfig.sort?.forEach((sort, ix) => {
                    const column = gridConfigMappedColumns.sortColumns[ix];

                    // TODO why was this needed?
                    if (!column)
                        return;

                    const predicate = column.sortPredicate;
                    const isSortedDescending = sort.isSortedDescending ?? false;

                    if (predicate)
                        ret = isSortedDescending
                            ? predicate(a, b, undefined, column) * -1
                            : predicate(a, b, undefined, column);
                });

                return ret;

            });

        const itemsPaged = items.slice((gridConfig.page - 1) * gridConfig.pageSize, gridConfig.page * gridConfig.pageSize);

        return [items, itemsPaged];
    },
        [
            scopeFlexibleFieldSets,
            snap,
            state.data.json.showInActives?.value,
            gridConfig.filter,
            gridConfig.sort,
            gridConfig.page,
        ]
    );

    const [selectedItems, setSelectedItems] = useState<FAM.IFlexibleFieldSet[] | null>(null);
    const selection = useRef(new Selection({
        onSelectionChanged: () => {
            const selectionCount = selection.current.getSelectedCount();
            setSelectionCount(selectionCount);
        },
        selectionMode: SelectionMode.multiple,
    }));

    const onRenderEditPanel = () => {
        if (!editorIsMounted)
            return <></>;

        return <AttributeSetGridViewEditor
            onDismiss={() => { setEditorIsOpen(false); }}
            disabled={props.disabled}
            category={props.category}
            defaultValue={ffs}
            isOpen={editorIsOpen}
            visibility={props.visibility}
            onChange={(attributeSet, changeTracker) => {

                if (attributeSet.id === 'bulk') {
                    if (!selectedItems)
                        return;

                    const selectedItemsCloned = structuredClone(selectedItems);

                    selectedItemsCloned.forEach(eClone => {
                        eClone.fields.forEach(eField => {
                            const matchingField = attributeSet.fields.find(q => q.configRef.id === eField.configRef.id);
                            if (matchingField) {
                                const value = matchingField.values[0].value;
                                if (value) {
                                    eField.values[0].value = value;
                                }
                            }
                        });

                        copyPropertiesOnly(attributeSet,
                            eClone,
                            ['deactivated'],
                            false,
                            (key, source) => {
                                if (source[key] === undefined)
                                    return false;

                                return true;
                            });

                    })

                    dispatch(addUpdateAttributeSet([{
                        category: props.category,
                        scope: props.scope,
                        attributeSets: selectedItemsCloned,
                        config: state.data.config,
                        changeTracker: changeTracker,
                    }]));

                    setSelectedItems(null);
                }
                else {

                    dispatch(addUpdateAttributeSet([{
                        category: props.category,
                        scope: props.scope,
                        attributeSets: [attributeSet],
                        config: state.data.config,
                        changeTracker: changeTracker,

                    }]));
                }

                setEditorIsOpen(false);
            }}
        />
    }

    const onSelect = (selectedItems: FAM.IFlexibleFieldSet[]) => {

        if (selectedItems && selectedItems.length === 1) {
            const _ffs = selectedItems[0];
            // clone in case we opt out of changing, to build a local state and disconnect from store
            setFFS(structuredClone(_ffs));
            setEditorIsOpen(true);

        } else if (selectedItems && selectedItems.length > 1) {
            const _ffs = createAttributeSet(state.data, ownerAttribute!);
            _ffs.id = 'bulk';
            setSelectedItems(selectedItems);
            setFFS(_ffs);
            setEditorIsOpen(true);
        }
    }

    useEffect(() => {
        if (history.location.search === '?navigation=pending')
            if (params.flexibleFieldSetId) {
                setTimeout(() => {
                    const flexibleFieldSet = scopeFlexibleFieldSets.find(t => t.id === params.flexibleFieldSetId);
                    if (flexibleFieldSet) {
                        // TODO might be necessary to check if flexibleFieldSetId belongs to current context
                        onSelect([flexibleFieldSet]);

                        if (!params.field2ndId && !params.tab2ndId && !params.section2ndId)
                            history.push({ pathname: history.location.pathname, search: '?navigation=complete' });
                    }
                }, 200);
            }
    }, []);

    const menu: IContextualMenuItem[] = [];

    if (!props.disabled)
        menu.push({
            key: `${props.label}PageAdd`,
            name: 'Add',
            icon: 'PageAdd',
            onClick: (event, item) => {
                if (event?.stopPropagation)
                    event.stopPropagation();

                if (!ownerAttribute)
                    throw new Error('Master attribute has not been identified.');

                const _ffs = createAttributeSet(state.data, ownerAttribute);

                setEditorIsOpen(true);
                setFFS(_ffs);
            }
        });

    if (!props.disabled)
        if (selectionCount)
            menu.push({
                key: `${props.label}PageEdit`,
                name: 'Edit',
                icon: 'PageEdit',
                onClick: (event, item) => {
                    if (event?.stopPropagation)
                        event.stopPropagation();

                    let selectedItems = selection.current.getSelection() as TFlexibleFieldSetGridItem[];

                    if (selection.current.isAllSelected())
                        selectedItems = items;

                    onSelect(selectedItems.map(q => q.data!));
                },
            });

    if (state.status.debug.developer)
        if (!props.disabled)
            if (selectionCount)
                menu.push({
                    key: `${props.label}Delete`,
                    name: 'Delete',
                    icon: 'Delete',
                    onClick: (event, item) => {

                        if (event?.stopPropagation)
                            event.stopPropagation();

                        let selectedItems = selection.current.getSelection() as TFlexibleFieldSetGridItem[];

                        if (selection.current.isAllSelected())
                            selectedItems = items;

                        const fieldSet = selectedItems.map(q => q.data!);
                        dispatch(addUpdateAttributeSet([{
                            category: props.category,
                            scope: props.scope,
                            attributeSets: fieldSet,
                            config: state.data.config,
                            changeTracker: {},
                            delete: true,
                        }]));

                    },
                });


    if (props.disabled)
        if (selectionCount === 1)
            menu.push({
                key: `${props.label}PageView`,
                name: 'View',
                icon: 'Page',
                onClick: (event, item) => {
                    if (event?.stopPropagation)
                        event.stopPropagation();

                    const selectedItems = selection.current.getSelection() as TFlexibleFieldSetGridItem[];

                    onSelect(selectedItems.map(q => q.data!));
                },
            });


    return (
        <>
            {onRenderEditPanel()}
            <CustomPanel
                title={`${props.label} (${items.length})`}
                id={`flexibleFieldSet.${props.configAttribute?.bcbs_config_attributeid!}`}
                canCollapse={true}
                style={{ marginTop: '15px' }}
                defaultCollapsed={(state.status.userConfigs['defaults.attributesets.defaultCollapsed'] as boolean) ?? items?.length < 1 ?? true}
                items={menu}
                mountWhenExpanded={true}
            >
                <CustomDetailList
                    columns={columns}
                    items={items}
                    itemsPaged={itemsPaged}
                    selectionMode={SelectionMode.multiple}
                    selection={selection.current}
                    defaultGridConfig={gridConfig}
                    quickSearchColumnKey={'attributeSetName'}
                    gridConfig={[gridConfig, setGridConfig]}
                    onItemInvoked={(item: TFlexibleFieldSetGridItem, index) => {
                        // to wait for render cycle to kick in first
                        // to open the right item, click was not passing in the right selected item.
                        setTimeout(() => {
                            const selectedItems = selection.current.getSelection() as TFlexibleFieldSetGridItem[];
                            onSelect(selectedItems.map(q => q.data!));
                        }, 60);

                    }}
                    onExtract={() => { DetailListExcelGenerationAsync(dispatch, state, application, columns, items) }}
                />
            </CustomPanel>
        </>
    );
}

export default AttributeSetGridView
